import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, FileDown, Upload, KeyRound, ArrowLeft } from "lucide-react";
import { BNTLogo } from "./layoutChrome";
import api, { formatApiError, tokenStore, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function RecuperacionExtravio() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [motivo, setMotivo] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [importInfo, setImportInfo] = useState(null);

  const downloadSolicitud = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (email.trim()) params.set("email", email.trim());
      if (motivo.trim()) params.set("motivo", motivo.trim());
      const qs = params.toString();
      const url = `${API}/system/password-recovery/solicitud-pdf${qs ? `?${qs}` : ""}`;
      const res = await api.get(url, { responseType: "blob", timeout: 60000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "solicitud-recuperacion-acceso.pdf";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF de solicitud descargado. Envíelo a su distribuidor.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el PDF");
    } finally {
      setBusy(false);
    }
  };

  const onImportPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/system/password-recovery/import-respuesta-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setImportInfo(data);
      toast.success(`Código registrado · ${data.uses_remaining} acceso(s) disponible(s)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo leer el PDF");
    } finally {
      setBusy(false);
    }
  };

  const activateCode = async () => {
    const tok = recoveryToken.trim();
    if (!tok) {
      toast.error("Pegue el código o importe el PDF de respuesta del distribuidor");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-recovery/activate", { recovery_token: tok });
      if (data.access_token) {
        tokenStore.set(data.access_token, data.refresh_token, { remember: false });
      }
      setUser(data);
      toast.success("Acceso temporal concedido. Defina su nueva contraseña.");
      navigate("/recuperacion-acceso", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Código inválido o agotado");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <BNTLogo size="sm" showDefault />
          <Link to="/login" className="text-xs text-[#0F2D5C] underline flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Volver al acceso
          </Link>
        </div>
        <div className="border-l-4 border-amber-500 pl-3">
          <h1 className="text-xl font-bold text-[#0F2D5C]">Recuperación por extravío</h1>
          <p className="text-xs text-slate-600 mt-1 leading-snug">
            Si olvidó la contraseña y las respuestas de seguridad, solicite un código temporal a su distribuidor.
          </p>
        </div>

        <section className="space-y-3 rounded-sm border border-slate-100 bg-slate-50/80 p-4">
          <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">1. Solicitud al distribuidor</p>
          <div>
            <Label className="text-xs">Correo de acceso (recomendado)</Label>
            <Input
              className="mt-1 h-9 text-sm font-mono"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@despacho.mx"
            />
          </div>
          <div>
            <Label className="text-xs">Motivo (opcional)</Label>
            <Input className="mt-1 h-9 text-sm" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <Button type="button" className="w-full h-9 bg-[#0F2D5C]" onClick={downloadSolicitud} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
            Descargar PDF de solicitud
          </Button>
        </section>

        <section className="space-y-3 rounded-sm border border-emerald-100 bg-emerald-50/50 p-4">
          <p className="text-[11px] font-semibold text-emerald-900 uppercase tracking-wide">
            2. Código del distribuidor
          </p>
          <label className="flex items-center justify-center gap-2 h-9 border border-dashed border-emerald-400 rounded-sm bg-white cursor-pointer text-xs text-emerald-900">
            <Upload className="w-4 h-4" />
            Importar PDF de respuesta
            <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={onImportPdf} disabled={busy} />
          </label>
          {importInfo ? (
            <p className="text-[10px] text-emerald-800">
              Registrado para <strong>{importInfo.email || "—"}</strong> · vigente hasta {importInfo.valid_until} ·{" "}
              {importInfo.uses_remaining} de {importInfo.uses_max} accesos
            </p>
          ) : null}
          <div>
            <Label className="text-xs">O pegue el código completo</Label>
            <textarea
              className="mt-1 w-full min-h-[72px] text-[10px] font-mono border border-slate-200 rounded-sm p-2"
              value={recoveryToken}
              onChange={(e) => setRecoveryToken(e.target.value)}
              placeholder="Código del PDF de respuesta…"
            />
          </div>
          <Button type="button" className="w-full h-9 bg-[#3DA873]" onClick={activateCode} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Usar código (consume 1 acceso)
          </Button>
        </section>
      </div>
    </div>
  );
}
